<template>

    <Title>Agenda: {{ meetingTitle }}</Title>

    <AgendaCard class="h-[90vh] overflow-hidden">
        <template #header>
            <div class="flex items-center justify-between w-full">
                <h3 class="text-lg font-medium text-gray-900">{{ meetingTitle }} Agenda</h3>
                <NuxtLink :to="`/${route.params.site_slug}/meetings/${route.params.meeting_slug}`">
                    <font-awesome class="w-4 h-4 mr-1"  :icon="['fas', 'xmark']" />
                </NuxtLink>
            </div>
        </template>
        <!-- Sidebar -->
        <div class="flex flex-col md:flex-row h-[83vh] rounded-lg overflow-hidden">
            <aside v-if="loadingComplete" class="w-full md:w-1/4 border-r border-r-gray-400 overflow-y-auto p-4">
                <ol class="agenda-list agenda-left"  :data-list_format="agendaListFormat">
                    <li v-for="(item, index) in agendaItems" :key="item.id">
                        <button
                            @click="scrollToItem(item.id)"
                            class="w-full text-left p-3 pl-10 rounded hover:bg-blue-100 transition font-medium"
                            :class="{ 'bg-primary-100 border border-primary': activeItem === item.id }"
                        >{{ item.text }}
                            <AttachmentsList
                                v-if="item.uploads && item.uploads.length && !item.is_private"
                                :attachments="item.uploads"
                                class="my-2 font-normal" context="agenda"
                                :inline="true"
                                :left-side="true"
                            ></AttachmentsList>
                        </button>
                        <ol v-if="item.agenda" class="ml-4 mt-1 space-y-1">
                            <li v-for="sub in item.agenda" :key="sub.id">
                                <button
                                    @click="scrollToItem(sub.id)"
                                    class="w-full text-left p-3 pl-12  rounded hover:bg-blue-50 transition font-medium"
                                    :class="{ 'bg-primary-100 border border-primary': activeItem === sub.id }"
                                >
                                    {{ sub.text }}
                                    <AttachmentsList
                                        v-if="sub.uploads && sub.uploads.length && !sub.is_private"
                                        :attachments="sub.uploads"
                                        class="my-2 font-normal" context="agenda"
                                        :inline="true"
                                        :left-side="true"
                                    ></AttachmentsList>
                                </button>
                                <ol v-if="sub.agenda" class="ml-6 mt-1 space-y-1">
                                    <li v-for="subsub in sub.agenda" :key="subsub.id">
                                        <button
                                            @click="scrollToItem(subsub.id)"
                                            class="w-full text-left p-3 pl-10 rounded hover:bg-blue-50 transition font-medium"
                                            :class="{ 'bg-primary-100 border border-primary': activeItem === subsub.id }"
                                        >
                                            {{ subsub.text }}
                                            <AttachmentsList
                                                v-if="subsub.uploads && subsub.uploads.length && !subsub.is_private"
                                                :attachments="subsub.uploads"
                                                class="my-2 font-normal" context="agenda"
                                                :inline="true"
                                                :left-side="true"
                                            ></AttachmentsList>
                                        </button>
                                    </li>
                                </ol>
                            </li>
                        </ol>
                    </li>
                </ol>
            </aside>
            <div v-else class="flex items-center justify-center w-full md:w-1/4 border-r border-r-gray-400 overflow-y-auto p-4">
                <font-awesome :icon="['fas', 'spinner']" class="animate-spin text-primary h-8 w-8" />
            </div>
            <!-- Main Content -->
            <main
                v-if="loadingComplete"
                class="w-full md:w-3/4 overflow-y-auto p-6 space-y-8 bg-gray-100 px-6 md:px-40"
                ref="content"
            >
                <ol class="space-y-6 agenda-list agenda-right"  :data-list_format="agendaListFormat">
                    <li v-for="(item, index) in agendaItems" :key="item.id" :id="item.id" :class="{ 'list-none': activeItem !== item.id}" class="!mt-0">
                        <div :class="{ 'hidden': activeItem !== item.id}">
                            <Card class="bg-white border rounded">
                                <template #header>
                                    <div class="flex items-center justify-between w-full p-4">
                                        <h3 class="text-lg font-medium ml-6">{{ item.text }}</h3>
                                        <h3 v-if="item.time && !item.is_private" class="text-sm ml-6 border border-gray-400 rounded-md py-0.5 px-1 text-gray-700">{{ item.time }}</h3>
                                    </div>
                                </template>
                                <div v-if="!item.is_private">
                                    <div class="block md:flex justify-between items-start gap-4">
                                        <p class="text-gray-700 flex-1 min-w-0 agenda-style" v-html="item.description"></p>
                                        <div v-if="(item.people_responsible && item.people_responsible.length) || item.duration" class="flex-shrink-0 flex flex-row items-start gap-4 mt-2 md:mt-0">
                                            <div v-if="item.people_responsible" class="flex flex-col gap-1">
                                                <p v-for="person in item.people_responsible" class="text-gray-700 whitespace-nowrap max-w-[150px] truncate">
                                                    {{ person.name }}
                                                </p>
                                            </div>
                                            <div v-if="item.duration" class="text-gray-700 whitespace-nowrap flex items-center">
                                                <font-awesome class="w-4 h-4 mr-2"  :icon="['far', 'clock']" />
                                                <span v-html="item.duration + ' minutes'"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div v-if="item.motion">
                                            <div v-for="motion in item.motion" >
                                                <div v-if="motion.motion_is_public" class="bg-gray-200 mt-5 p-5 rounded-md w-full">
                                                    <p class="mb-4">{{ motion.motion_text }}</p>
                                                    <p class="mb-4">
                                                        <span class="font-medium">Moved by:</span> {{ motion.motion_moved_by }}<br />
                                                        <span class="font-medium">Seconded by:</span> {{ motion.motion_seconded_by }}<br />
                                                        <span class="font-medium">Outcome:</span> {{ motion.motion_decision }}<br />
                                                    </p>
                                                    <p>{{ formattedVotes(motion.votes) }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <AttachmentsList
                                    v-if="item.uploads && item.uploads.length && !item.is_private"
                                    :attachments="item.uploads"
                                    class="my-2 font-normal -ml-2" context="agenda"
                                    :inline="true"
                                    :right-side="true"
                                ></AttachmentsList>
                            </Card>
                            <div v-if="item.uploads && item.uploads.length && !item.is_private" v-for="upload in item.uploads"> 
                                <div  class="flex flex-col items-center">
                                    <div class="flex p-2 border border-gray-400 rounded-md bg-gray-50 text-center w-auto max-w-full bg-white mt-6">
                                        <img :src="upload.bareIconUrl" class="w-4 mr-2" alt="" aria-hidden="true" />
                                        <a class="flex" :href="config.public.boardDomain + '/' + publicSiteData?.organization_slug + '/public-site-data/meetings/' + route.params.meeting_slug + '/documents/' + upload.uuid.short_slug + '/view'" target="_blank" :aria-label="`${upload.title} (opens in new tab)`">
                                            <span class="mr-2 block truncate max-w-[180px] md:max-w-[220px]">{{ upload.title }}</span>
                                        </a>
                                        <font-awesome v-if="expanded[upload.id]" @click.prevent="toggle(upload.id)" class="w-4 h-4 mr-2 mt-1"  :icon="['fas', 'caret-down']" />
                                        <font-awesome v-else @click.prevent="toggle(upload.id)" class="w-4 h-4 mr-2 mt-1"  :icon="['fas', 'caret-up']" />
                                    </div>
                                </div>
                                <div v-if="expanded[upload.id] && upload.preSignedUrl && isPdf(upload)" class="flex justify-end items-center gap-2 mt-4">
                                    <button
                                        type="button"
                                        class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                        aria-haspopup="dialog"
                                        :disabled="processingAccessible[upload.id]"
                                        @click="openDownloadModal(upload)"
                                    >
                                        <span>Download</span>
                                        <font-awesome :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
                                    </button>
                                </div>
                                <AdobePdfViewer
                                    v-if="expanded[upload.id] && upload.preSignedUrl && adobeEnabled && isPdf(upload) && !adobeFailed[upload.id]"
                                    :key="`adobe-${upload.id}-${accessibleEnabled}`"
                                    :src="getRawIframeSrc(upload)"
                                    :pdf-title="upload.title"
                                    container-class="w-full h-screen border border-gray-400 left-0 rounded-md shadow-2xl mt-6"
                                    :show-download="false"
                                    @failed="adobeFailed[upload.id] = true"
                                />
                                <iframe
                                    v-else-if="expanded[upload.id] && upload.preSignedUrl"
                                    :key="`iframe-${upload.id}-${accessibleEnabled}`"
                                    :src="getIframeSrc(upload)"
                                    :title="`Attachment preview: ${upload.title}`"
                                    class="w-full h-screen border border-gray-400 left-0 rounded-md shadow-2xl mt-6"
                                    @load="handleIframeLoad(upload)"
                                />
                                <div v-else-if="expanded[upload.id] && !upload.presigned_url" class="mt-6">
                                    <div class="bg-white w-full p-8 flex flex-col items-center justify-center text-center rounded-md border border-gray-400">
                                        <p class="mb-4">Attachment preview unavailable</p>
                                        <Button type="primary" button-type="button" @click="openInNewTab(config.public.boardDomain + '/' + publicSiteData?.organization_slug + '/public-site-data/meetings/' + route.params.meeting_slug + '/documents/' + upload.uuid.short_slug + '/view')">View attachment here</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ol v-if="item.agenda" class="ml-2 space-y-6">
                            <li v-for="sub in item.agenda" :key="sub.id" :id="sub.id" :class="{ 'list-none': activeItem !== sub.id}" class="!mt-0">
                                <div :class="{ 'hidden': activeItem !== sub.id}">
                                    <Card class="bg-white border rounded">
                                        <template #header>
                                            <div class="flex items-center justify-between w-full p-4">
                                                <h3 class="text-lg font-medium ml-6">{{  sub.text  }}</h3>
                                                <h3 v-if="sub.time && !sub.is_private" class="text-sm ml-6 border border-gray-400 rounded-md py-0.5 px-1 text-gray-700">{{ sub.time }}</h3>
                                            </div>
                                        </template>
                                        <div v-if="!sub.is_private">
                                            <div class="block md:flex justify-between items-start gap-4">
                                                <p class="text-gray-700 flex-1 min-w-0 agenda-style" v-html="sub.description"></p>
                                                <div v-if="(sub.people_responsible && sub.people_responsible.length) || sub.duration" class="flex-shrink-0 flex flex-row items-start gap-4 mt-2 md:mt-0">
                                                    <div v-if="sub.people_responsible" class="flex flex-col gap-1">
                                                        <p v-for="person in sub.people_responsible" class="text-gray-700 whitespace-nowrap max-w-[150px] truncate">
                                                            {{ person.name }}
                                                        </p>
                                                    </div>
                                                    <div v-if="sub.duration" class="text-gray-700 whitespace-nowrap flex items-center">
                                                        <font-awesome class="w-4 h-4 mr-2"  :icon="['far', 'clock']" />
                                                        <span v-html="sub.duration + ' minutes'"></span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <div v-if="sub.motion">
                                                    <div v-for="motion in sub.motion" >
                                                        <div v-if="motion.motion_is_public" class="bg-gray-200 mt-5 p-5 rounded-md w-full">
                                                            <p class="mb-4">{{ motion.motion_text }}</p>
                                                            <p class="mb-4">
                                                                <span class="font-medium">Moved by:</span> {{ motion.motion_moved_by }}<br />
                                                                <span class="font-medium">Seconded by:</span> {{ motion.motion_seconded_by }}<br />
                                                                <span class="font-medium">Outcome:</span> {{ motion.motion_decision }}<br />
                                                            </p>
                                                            <p>{{ formattedVotes(motion.votes) }}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <AttachmentsList
                                            v-if="sub.uploads && sub.uploads.length && !sub.is_private"
                                            :attachments="sub.uploads"
                                            class="my-2 font-normal -ml-2" context="agenda"
                                            :inline="true"
                                            :right-side="true"
                                        ></AttachmentsList>
                                    </Card>
                                    <div v-if="sub.uploads && sub.uploads.length && !sub.is_private" v-for="upload in sub.uploads">
                                        <div  class="flex flex-col items-center">
                                            <div class="flex p-2 border border-gray-400 rounded-md bg-gray-50 text-center w-auto max-w-full bg-white mt-6">
                                                <img :src="upload.bareIconUrl" class="w-4 mr-2" alt="" aria-hidden="true" />
                                                <a class="flex" :href="config.public.boardDomain + '/' + publicSiteData?.organization_slug + '/public-site-data/meetings/' + route.params.meeting_slug + '/documents/' + upload.uuid.short_slug + '/view'" target="_blank" :aria-label="`${upload.title} (opens in new tab)`">
                                                    <span class="mr-2 block truncate max-w-[180px] md:max-w-[220px]">{{ upload.title }}</span>
                                                </a>
                                                <font-awesome v-if="expanded[upload.id]" @click.prevent="toggle(upload.id)" class="w-4 h-4 mr-2 mt-1"  :icon="['fas', 'caret-down']" />
                                                <font-awesome v-else @click.prevent="toggle(upload.id)" class="w-4 h-4 mr-2 mt-1"  :icon="['fas', 'caret-up']" />
                                            </div>
                                        </div>
                                        <div v-if="expanded[upload.id] && upload.preSignedUrl && isPdf(upload)" class="flex justify-end items-center gap-2 mt-4">
                                            <button
                                                type="button"
                                                class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                                aria-haspopup="dialog"
                                                :disabled="processingAccessible[upload.id]"
                                                @click="openDownloadModal(upload)"
                                            >
                                                <span>Download</span>
                                                <font-awesome :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
                                            </button>
                                        </div>
                                        <AdobePdfViewer
                                            v-if="expanded[upload.id] && upload.preSignedUrl && adobeEnabled && isPdf(upload) && !adobeFailed[upload.id]"
                                            :key="`adobe-${upload.id}-${accessibleEnabled}`"
                                            :src="getRawIframeSrc(upload)"
                                            :pdf-title="upload.title"
                                            container-class="w-full h-screen border border-gray-400 left-0 rounded-md shadow-2xl mt-6"
                                            :show-download="false"
                                            @failed="adobeFailed[upload.id] = true"
                                        />
                                        <iframe
                                            v-else-if="expanded[upload.id] && upload.preSignedUrl"
                                            :key="`iframe-${upload.id}-${accessibleEnabled}`"
                                            :src="getIframeSrc(upload)"
                                            :title="`Attachment preview: ${upload.title}`"
                                            class="w-full h-screen border border-gray-400 left-0 rounded-md shadow-2xl mt-6"
                                            @load="handleIframeLoad(upload)"
                                        />
                                        <div v-else-if="expanded[upload.id] && !upload.presigned_url" class="mt-6">
                                            <div class="bg-white w-full p-8 flex flex-col items-center justify-center text-center rounded-md border border-gray-400">
                                                <p class="mb-4">Attachment preview unavailable</p>
                                                <Button type="primary" button-type="button" @click="openInNewTab(config.public.boardDomain + '/' + publicSiteData?.organization_slug + '/public-site-data/meetings/' + route.params.meeting_slug + '/documents/' + upload.uuid.short_slug + '/view')">View attachment here</Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <ol v-if="sub.agenda" class="ml-4 space-y-6">
                                    <li v-for="subsub in sub.agenda" :key="subsub.id" :id="subsub.id" :class="{ 'list-none': activeItem !== subsub.id}" class="!mt-0">
                                        <div :class="{ 'hidden': activeItem !== subsub.id}">
                                            <Card class="bg-white border rounded shadow">
                                                <template #header>
                                                    <div class="flex items-center justify-between w-full p-4">
                                                        <h3 class="text-lg font-medium ml-6">{{ subsub.text }}</h3>
                                                        <h3 v-if="subsub.time && !subsub.is_private" class="text-sm ml-6 border border-gray-400 rounded-md py-0.5 px-1 text-gray-700">{{ subsub.time }}</h3>
                                                    </div>
                                                </template>
                                                <div v-if="!subsub.is_private">
                                                    <div class="block md:flex justify-between items-start gap-4">
                                                        <p class="text-gray-700 flex-1 min-w-0 agenda-style" v-html="subsub.description"></p>
                                                        <div v-if="(subsub.people_responsible && subsub.people_responsible.length) || subsub.duration" class="flex-shrink-0 flex flex-row items-start gap-4 mt-2 md:mt-0">
                                                            <div v-if="subsub.people_responsible" class="flex flex-col gap-1">
                                                                <p v-for="person in subsub.people_responsible" class="text-gray-700 whitespace-nowrap max-w-[150px] truncate">
                                                                    {{ person.name }}
                                                                </p>
                                                            </div>
                                                            <div v-if="subsub.duration" class="text-gray-700 whitespace-nowrap flex items-center">
                                                                <font-awesome class="w-4 h-4 mr-2"  :icon="['far', 'clock']" />
                                                                <span v-html="subsub.duration + ' minutes'"></span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div v-if="subsub.motion">
                                                            <div v-for="motion in subsub.motion" >
                                                                <div v-if="motion.motion_is_public" class="bg-gray-200 mt-5 p-5 rounded-md w-full">
                                                                    <p class="mb-4">{{ motion.motion_text }}</p>
                                                                    <p class="mb-4">
                                                                        <span class="font-medium">Moved by:</span> {{ motion.motion_moved_by }}<br />
                                                                        <span class="font-medium">Seconded by:</span> {{ motion.motion_seconded_by }}<br />
                                                                        <span class="font-medium">Outcome:</span> {{ motion.motion_decision }}<br />
                                                                    </p>
                                                                    <p>{{ formattedVotes(motion.votes) }}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <AttachmentsList
                                                    v-if="subsub.uploads && subsub.uploads.length && !subsub.is_private"
                                                    :attachments="subsub.uploads"
                                                    class="my-2 font-normal -ml-2" context="agenda"
                                                    :inline="true"
                                                    :right-side="true"
                                                ></AttachmentsList>
                                            </Card>
                                            <div v-if="subsub.uploads && subsub.uploads.length && !subsub.is_private" v-for="upload in subsub.uploads">
                                                <div  class="flex flex-col items-center">
                                                    <div class="flex p-2 border border-gray-400 rounded-md bg-gray-50 text-center w-auto max-w-full bg-white mt-6">
                                                        <img :src="upload.bareIconUrl" class="w-4 mr-2" alt="" aria-hidden="true" />
                                                        <a class="flex" :href="config.public.boardDomain + '/' +  publicSiteData?.organization_slug + '/public-site-data/meetings/' + route.params.meeting_slug + '/documents/' + upload.uuid.short_slug + '/view'" target="_blank" :aria-label="`${upload.title} (opens in new tab)`">
                                                            <span class="mr-2 block truncate max-w-[180px] md:max-w-[220px]">{{ upload.title }}</span>
                                                        </a>
                                                        <font-awesome v-if="expanded[upload.id]" @click.prevent="toggle(upload.id)" class="w-4 h-4 mr-2 mt-1"  :icon="['fas', 'caret-down']" />
                                                        <font-awesome v-else @click.prevent="toggle(upload.id)" class="w-4 h-4 mr-2 mt-1"  :icon="['fas', 'caret-up']" />
                                                    </div>
                                                </div>
                                                <div v-if="expanded[upload.id] && upload.preSignedUrl && isPdf(upload)" class="flex justify-end items-center gap-2 mt-4">
                                                    <button
                                                        type="button"
                                                        class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                                                        aria-haspopup="dialog"
                                                        :disabled="processingAccessible[upload.id]"
                                                        @click="openDownloadModal(upload)"
                                                    >
                                                        <span>Download</span>
                                                        <font-awesome :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
                                                    </button>
                                                </div>
                                                <AdobePdfViewer
                                                    v-if="expanded[upload.id] && upload.preSignedUrl && adobeEnabled && isPdf(upload) && !adobeFailed[upload.id]"
                                                    :key="`adobe-${upload.id}-${accessibleEnabled}`"
                                                    :src="getRawIframeSrc(upload)"
                                                    :pdf-title="upload.title"
                                                    container-class="w-full h-screen border border-gray-400 left-0 rounded-md shadow-2xl mt-6"
                                                    :show-download="false"
                                                    @failed="adobeFailed[upload.id] = true"
                                                />
                                                <iframe
                                                    v-else-if="expanded[upload.id] && upload.preSignedUrl"
                                                    :key="`iframe-${upload.id}-${accessibleEnabled}`"
                                                    :src="getIframeSrc(upload)"
                                                    :title="`Attachment preview: ${upload.title}`"
                                                    class="w-full h-screen border border-gray-400 left-0 rounded-md shadow-2xl mt-6"
                                                    @load="handleIframeLoad(upload)"
                                                />
                                                <div v-else-if="expanded[upload.id] && !upload.presigned_url" class="mt-6">
                                                    <div class="bg-white w-full p-8 flex flex-col items-center justify-center text-center rounded-md border border-gray-400">
                                                        <p class="mb-4">Attachment preview unavailable</p>
                                                        <Button type="primary" button-type="button" @click="openInNewTab(config.public.boardDomain + '/' + route.params.org_slug + '/public-site-data/meetings/' + route.params.meeting_slug + '/documents/' + upload.uuid.short_slug + '/view')">View attachment here</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ol>
                            </li>
                        </ol>
                    </li>
                </ol>
            </main>
            <div v-else class="flex items-center justify-center w-full">
                <font-awesome :icon="['fas', 'spinner']" class="animate-spin text-primary h-8 w-8" />
            </div>
        </div>
    </AgendaCard>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue';
import AgendaCard from "~/components/ui/AgendaCard.vue";
import Card from "~/components/ui/Card.vue";
import Button from "~/components/ui/Button.vue";
import AttachmentsList from "~/components/agenda/AttachmentsList.vue";
import { useModal } from 'vue-final-modal';
import AdobePdfViewer from "~/components/pdf/AdobePdfViewer.vue";
import DownloadPdfModal from "~/components/modals/DownloadPdfModal.vue";

const openInNewTab = (url) => {
    if (typeof window !== 'undefined') {
        window.open(url, '_blank');
    }
};

const config = useRuntimeConfig();
const route = useRoute()
const agendaItems = ref([])
const agendaListFormat = ref('')
const meetingTitle = ref('')
const current_date = ref(null)
const date = route.query.date
const expanded = ref([])
const publicSiteData = useState('publicSiteData');
const loadingComplete = ref(false)

// Accessible PDF preference (shared cookie across subdomains)
const { accessibleEnabled, featureEnabled: accessibleFeatureEnabled } = useAccessiblePdfPreference();

const accessibleUrls = ref({});
const processingAccessible = ref({});

// Prefetch accessible PDFs for expanded items when toggle is on
const prefetchAccessiblePdfs = () => {
    if (!accessibleEnabled.value || !agendaItems.value?.length) return;
    const allUploads = collectUploads(agendaItems.value);
    for (const upload of allUploads) {
        if (isPdf(upload) && expanded.value[upload.id] && !accessibleUrls.value[upload.id] && !processingAccessible.value[upload.id]) {
            getUploadUrl(upload); // Don't await - let them fetch in parallel
        }
    }
};

// Prefetch when feature is enabled
watch(accessibleEnabled, () => {
    if (accessibleEnabled.value) {
        prefetchAccessiblePdfs();
    }
});

// Prefetch on mount if toggle already on
onMounted(() => {
    prefetchAccessiblePdfs();
});

// Check if file is a PDF
const isPdf = (upload) => {
    return upload.preSignedUrl && (
        upload.preSignedUrl.toLowerCase().includes('.pdf') ||
        upload.extension?.toLowerCase() === 'pdf' ||
        upload.mime_type?.toLowerCase().includes('pdf')
    );
};

// Append PDF Open Parameters to hide the native browser PDF toolbar and
// fit the page width to the viewer on load (fixes mobile zoom-in issue).
// Works in Chromium (PDFium) and Firefox (PDF.js); Safari ignores both.
// Safe with signed S3 URLs because fragments are never sent to the server.
const withPdfViewerParams = (url) => {
    if (!url) return url;
    return url + (url.includes('#') ? '&' : '#') + 'toolbar=0&view=FitH';
};

// Get the raw URL for an upload (no PDF viewer params). Used by AdobePdfViewer.
const getRawIframeSrc = (upload) => {
    if (!accessibleEnabled.value || !isPdf(upload)) {
        return upload.preSignedUrl;
    }
    // Return accessible URL if available, otherwise fall back to original
    return accessibleUrls.value[upload.id] || upload.preSignedUrl;
};

// Get the appropriate iframe src for an upload (native iframe path)
const getIframeSrc = (upload) => {
    const baseUrl = getRawIframeSrc(upload);
    // Only PDFs honour the viewer params; leave other previews alone.
    return isPdf(upload) ? withPdfViewerParams(baseUrl) : baseUrl;
};

// Which viewer to render (Adobe PDF Embed or native browser iframe)
const { adobeEnabled } = usePdfViewer();

// Crash guard: per-upload flag, set when AdobePdfViewer emits 'failed' for
// that upload. The template falls back to the native iframe for that upload.
const adobeFailed = reactive({});

// Build Laravel download URL for an upload (has Content-Disposition: attachment)
const getDownloadUrl = (upload) => {
    return `${config.public.boardDomain}/${publicSiteData.value?.organization_slug}/public-site-data/meetings/${route.params.meeting_slug}/documents/${upload.uuid.short_slug}/download`;
};

// Open download modal for a specific upload
const openDownloadModal = (upload) => {
    const attrs = reactive({
        showAccessibleOption: accessibleFeatureEnabled.value,
        loading: false,
        onClose() {
            if (attrs.loading) return;
            close();
        },
        async onDownload(type) {
            attrs.loading = true;
            try {
                if (type === 'accessible') {
                    try {
                        if (!accessibleUrls.value[upload.id]) {
                            await getUploadUrl(upload);
                        }
                        if (accessibleUrls.value[upload.id]) {
                            const link = document.createElement('a');
                            link.href = accessibleUrls.value[upload.id] + '?download';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                    } catch (err) {
                        console.error('Accessible PDF download failed, falling back to original:', err);
                        const link = document.createElement('a');
                        link.href = getDownloadUrl(upload);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                } else {
                    const link = document.createElement('a');
                    link.href = getDownloadUrl(upload);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } finally {
                attrs.loading = false;
                close();
            }
        }
    });

    const { open, close } = useModal({
        component: DownloadPdfModal,
        attrs
    });
    open();
};

// Get the appropriate URL for the upload (uses signed URL approach - no blob URLs)
const getUploadUrl = async (upload) => {
    if (!isPdf(upload) || !accessibleEnabled.value) {
        return upload.preSignedUrl;
    }

    // Return cached accessible URL if available
    if (accessibleUrls.value[upload.id]) {
        return accessibleUrls.value[upload.id];
    }

    // Get signed URL if not cached
    if (!processingAccessible.value[upload.id]) {
        processingAccessible.value[upload.id] = true;

        try {
            // Request signed URL instead of blob
            const response = await $fetch('/public-api/pdf/accessible', {
                method: 'POST',
                body: {
                    pdfUrl: upload.preSignedUrl,
                    title: upload.title || 'Agenda Attachment',
                    lang: 'en-US',
                    format: 'url',
                    siteSlug: route.params.site_slug,
                    pageSlug: route.params.meeting_slug,
                    pageType: 'agenda'
                }
            });

            accessibleUrls.value[upload.id] = response.url;
            return response.url;
        } catch (err) {
            console.error('Error getting accessible PDF URL:', err);
            return upload.preSignedUrl;
        } finally {
            processingAccessible.value[upload.id] = false;
        }
    }

    return upload.preSignedUrl;
};

const toggle = (index) => {
    expanded.value[index] = !expanded.value[index]
};

const formattedVotes = (motionVotes) => {
    return Object.values(motionVotes || {})
        .filter(v => v?.motion_vote_vote)
        .map(v => `${v.motion_vote_user} (${v.motion_vote_vote})`)
        .join(', ')
}

// Handle iframe load - fetch accessible PDF if not already prefetched
// This handles the case where a PDF is expanded after the toggle was turned on
const handleIframeLoad = (upload) => {
    // Only fetch if: accessible mode is on, it's a PDF, not already cached, not currently processing
    if (accessibleEnabled.value && isPdf(upload) && !accessibleUrls.value[upload.id] && !processingAccessible.value[upload.id]) {
        getUploadUrl(upload);
    }
};


const fetchAgenda = async (date = null) => {

    let url = '/public-sites/meetings/' + route.params.meeting_slug + '/agenda';

    if(date){
        url += '?date=' + date
    }

    const response = await publicFetch(url);

    if (response?.data) {
        agendaItems.value = response.data.agenda
        agendaListFormat.value = response.data.agenda_list_format
        meetingTitle.value = response.data.meeting_title
        current_date.value = date

        if (agendaItems.value.length) {
            activeItem.value = agendaItems.value[0].id;
            // Only expand attachments for the first section on initial load
            expandUploadsForItem(agendaItems.value[0]);
        }
        loadingComplete.value = true
    } else {
        console.warn('No data')
    }
}

function collectUploads(items) {
    const uploads = []

    const traverse = (itemList) => {
        itemList.forEach(item => {
            // Collect uploads at current level
            if (item.uploads && Array.isArray(item.uploads)) {
                uploads.push(...item.uploads)
            }

            // Recursively check for children
            if (item.agenda && Array.isArray(item.agenda)) {
                traverse(item.agenda)
            }
        })
    }

    traverse(items)

    return uploads
}

const flattenedItems = computed(() => {
    const flatten = (items) => {
        return items.flatMap(item => [
            {
                id: item.id,
                text: item.text,
                description: item.description
            },
            ...(item.agenda ? flatten(item.agenda) : [])
        ]);
    };

    return flatten(agendaItems.value);
});

const content = ref(null);
const activeItem = ref(null);

// Helper to find an item by id in nested agenda structure
const findItemById = (items, id) => {
    for (const item of items) {
        if (item.id === id) return item;
        if (item.agenda) {
            const found = findItemById(item.agenda, id);
            if (found) return found;
        }
    }
    return null;
};

// Expand uploads for a given item
const expandUploadsForItem = (item) => {
    if (item?.uploads) {
        item.uploads.forEach(upload => {
            expanded.value[upload.id] = true;
        });
    }
};

const scrollToItem = (id) => {
    const container = content.value;
    const el = document.getElementById(id);

    if (container && el) {
        activeItem.value = id;
        // Expand attachments for this section when clicked
        const item = findItemById(agendaItems.value, id);
        expandUploadsForItem(item);
    }
    container.scrollTop = 0;
};

fetchAgenda(date);
</script>
